import { AxiosResponse } from 'axios';

import { useMembersContext } from '@/contexts/membersContext';
import { Me } from '@/pages/members/types';
import ajax from '@/utils/ajax';

interface GetOneTimeTokenResponse {
  one_time_token: string;
  uid: string;
  link: string;
}

export const authRepository = {
  async login({
    host,
    username,
    password
  }: {
    host: string;
    username: string;
    password: string;
  }): Promise<Me> {
    const path = `/api/members/login/`;
    const response = await ajax.post<{ username: string; password: string }, AxiosResponse<Me>>({
      url: `${host}${path}`,
      data: { username, password },
      config: {
        withCredentials: true
      }
    });

    return response.data;
  },

  async logout({ host }: { host: string }): Promise<void> {
    const path = `/api/members/logout/`;
    await ajax.post({
      url: `${host}${path}`,
      data: {},
      config: {
        withCredentials: true,
        withXSRFToken: true,
        xsrfCookieName: 'csrftoken',
        xsrfHeaderName: 'X-CSRFToken'
      }
    });
  },

  async getOneTimeToken({ host }: { host: string }): Promise<GetOneTimeTokenResponse> {
    const path = `/api/users/auth/one_time_login`;
    const response = await ajax.get({
      url: `${host}${path}`,
      config: {
        withCredentials: true
      }
    });
    return response.data;
  },

  async sendOneTimeToken({ host }: { host: string }): Promise<void> {
    const path = `/api/users/auth/one_time_login`;
    await ajax.post({
      url: `${host}${path}`,
      data: {},
      config: {
        withCredentials: true,
        withXSRFToken: true,
        xsrfCookieName: 'csrftoken',
        xsrfHeaderName: 'X-CSRFToken'
      }
    });
  },

  async exchangeOneTimeToken({
    host,
    uid,
    token
  }: {
    host: string;
    uid: string;
    token: string;
  }): Promise<void> {
    const path = `/api/users/auth/one_time_login/${uid}/${token}/`;
    await ajax.post({
      url: `${host}${path}`,
      data: {},
      config: {
        withCredentials: true
      }
    });
  }
};

const AuthRepository = () => {
  const { host } = useMembersContext();

  return {
    async login(username: string, password: string): Promise<Me> {
      return authRepository.login({ host, username, password });
    },
    async logout(): Promise<void> {
      return authRepository.logout({ host });
    },
    async getOneTimeToken(): Promise<GetOneTimeTokenResponse> {
      return authRepository.getOneTimeToken({ host });
    },
    async sendOneTimeToken(): Promise<void> {
      return authRepository.sendOneTimeToken({ host });
    },
    async exchangeOneTimeToken(uid: string, token: string): Promise<void> {
      return authRepository.exchangeOneTimeToken({ host, uid, token });
    }
  };
};

export default AuthRepository;
